import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, ArrowLeft, Activity, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Analytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics`, {
        credentials: 'include'
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      toast.error('Помилка завантаження аналітики');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">Аналітика</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
              >
                <Activity className="w-8 h-8 text-primary mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{analytics?.total_xp}</div>
                <p className="text-sm text-muted-foreground">Загально XP</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
              >
                <TrendingUp className="w-8 h-8 text-accent mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{analytics?.level}</div>
                <p className="text-sm text-muted-foreground">Поточний рівень</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
              >
                <Activity className="w-8 h-8 text-secondary mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{analytics?.games_played}</div>
                <p className="text-sm text-muted-foreground">Зіграно ігор</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
              >
                <CheckCircle className="w-8 h-8 text-accent mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{analytics?.accuracy}%</div>
                <p className="text-sm text-muted-foreground">Точність</p>
              </motion.div>
            </div>

            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-foreground mb-6">Ваш прогрес</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics?.recent_sessions?.map((s, i) => ({
                    name: `Гра ${i + 1}`,
                    xp: s.xp_earned || 0
                  })) || []}>
                    <defs>
                      <linearGradient id="colorXp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="name" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#121212',
                        border: '1px solid #27272A',
                        borderRadius: '8px'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="xp"
                      stroke="#3B82F6"
                      fillOpacity={1}
                      fill="url(#colorXp)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {analytics?.recent_sessions && analytics.recent_sessions.length > 0 && (
              <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-foreground mb-6">Останні сесії</h3>
                <div className="space-y-4">
                  {analytics.recent_sessions.slice(0, 5).map((session, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card/30"
                    >
                      <div>
                        <h4 className="font-bold text-foreground capitalize">{session.mode}</h4>
                        <p className="text-sm text-muted-foreground">
                          {session.correct_count || 0} / {session.questions_answered || 0} правильних
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">+{session.xp_earned || 0} XP</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}